// MSSQL identifier helpers: bracket/quoted identifier round-trip.
// Extracted from `sqlToolsLanguageServiceProvider.ts` (lines 63–75 in the
// pre-resolver-slice version) so the dialect-native MSSQL resolver can
// re-use the same quoting rules that the deleted SSTS supplementation used.
//
// MSSQL identifier rules (with `QUOTED_IDENTIFIER ON`, the default):
//   * Bare identifiers match `^[A-Za-z_][A-Za-z0-9_]*$`.
//   * Anything else needs `[bracketing]` (the asymmetric MSSQL form) — the
//     `]` inside the body is escaped by doubling.
//   * `"double-quotes"` are also valid identifier delimiters when
//     QUOTED_IDENTIFIER is ON. The resolver outputs `[…]` form by default
//     because that's the SSMS-canonical shape and survives even when
//     QUOTED_IDENTIFIER is OFF.
//
// Pure module — no Monaco runtime, no I/O.

const BARE_IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function needsBracketQuote(identifier: string): boolean {
  return !BARE_IDENT_RE.test(identifier);
}

export function bracketQuote(identifier: string): string {
  return `[${identifier.replace(/]/g, ']]')}]`;
}

/** Round-trip-safe MSSQL identifier — bracketed only when required. */
export function formatIdentifier(identifier: string): string {
  return needsBracketQuote(identifier) ? bracketQuote(identifier) : identifier;
}

/** Compose `[schema].[name]` with per-part bracket-on-demand. */
export function formatQualifiedTwoPart(schema: string, name: string): string {
  return `${formatIdentifier(schema)}.${formatIdentifier(name)}`;
}

/**
 * Strip the surrounding `[…]` / `"…"` delimiters from a single identifier
 * token, applying the right doubled-escape per delimiter form. Used by the
 * resolver when matching alias bindings (the binding's `relation` is stored
 * verbatim, including any quote chars; the snapshot's tables are stored
 * bare). Mirrors the unquoting logic in `resolver/identifierUtils.ts:18-37`
 * but is kept dialect-local because the MSSQL resolver is the only consumer.
 */
export function unquoteIdentifier(token: string): string {
  if (token.length < 2) return token;
  const first = token[0]!;
  const last = token[token.length - 1]!;
  if (first === '[' && last === ']') {
    return token.slice(1, -1).replace(/]]/g, ']');
  }
  if (first === '"' && last === '"') {
    return token.slice(1, -1).replace(/""/g, '"');
  }
  return token;
}

export function eqIdentifier(a: string, b: string): boolean {
  return unquoteIdentifier(a).toLowerCase() === unquoteIdentifier(b).toLowerCase();
}
